import { useCallback, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { VISIOUS_SCOPES } from '@visious/core';
import type { AuthApiProblem } from '../api/auth';
import { clearHistory, markWatched, removeFromHistory, type HistoryProblem } from '../api/history';
import { getPlatform } from '../platform/instance';
import { useSession } from '../platform/session';
import { invalidateHistory, markHistoryStale } from './useHistoryList';

/**
 * The three history writes, as hooks — and **not** `useMutation`, for the reason `playlists/mutations.ts`
 * sets out at length: this codebase's transport returns failures as values, and wrapping each call in a
 * throw so a library can catch it and hand back an `unknown` is three conversions to arrive where the
 * call already was. What is left is a function returning a `Result`, a busy flag, and the invalidation.
 *
 * **The grant is passed here, unlike on the read.** `api/auth.ts` has the rule: a mutation the token
 * cannot make must not reach the wire, because `Invalid scope` and `Token is expired` share a status and
 * "did it happen?" is not a question a retry answers. For `clearHistory` that reasoning is at its
 * strongest — a call that might have destroyed everything is not one to fire speculatively.
 */

export type WriteResult<E = HistoryProblem> = { readonly ok: true } | { readonly ok: false; readonly problem: E };

const BAD_PLATFORM: AuthApiProblem = { kind: 'bad-url', path: '/api/v1/auth/history' };

function useGrantKey(): number {
  return useSession().grantedAtEpochSec ?? 0;
}

/**
 * One in-flight write per key, so each row can show its own state — removing row 4 must not grey out
 * row 5. Same shape as the playlist mutations' busy set, and the same reason.
 */
function useBusyKeys(): {
  readonly busy: ReadonlySet<string>;
  readonly run: <T>(key: string, work: () => Promise<T>) => Promise<T>;
} {
  const [busy, setBusy] = useState<ReadonlySet<string>>(EMPTY);

  const run = useCallback(async <T,>(key: string, work: () => Promise<T>): Promise<T> => {
    setBusy((previous) => new Set(previous).add(key));
    try {
      return await work();
    } finally {
      setBusy((previous) => {
        const next = new Set(previous);
        next.delete(key);
        return next;
      });
    }
  }, []);

  return { busy, run };
}

const EMPTY: ReadonlySet<string> = new Set();

export interface HistoryMutations {
  /** Remove one row. Keyed by video id, so the busy set addresses a single card. */
  readonly remove: (videoId: string) => Promise<WriteResult>;
  /** Clear the whole history — **one** request, not one per row. */
  readonly clear: () => Promise<WriteResult<AuthApiProblem>>;
  readonly busy: ReadonlySet<string>;
  readonly clearing: boolean;
}

/** The key the clear-all write occupies in the busy set. Not a video id, so it cannot collide with one. */
const CLEAR_KEY = 'clear-all';

export function useHistoryMutations(): HistoryMutations {
  const client = useQueryClient();
  const grantKey = useGrantKey();
  const { busy, run } = useBusyKeys();

  const remove = useCallback(
    async (videoId: string): Promise<WriteResult> =>
      await run(videoId, async () => {
        const boot = await getPlatform();
        if (!boot.ok) return { ok: false as const, problem: BAD_PLATFORM };

        const result = await removeFromHistory(boot.platform, videoId, VISIOUS_SCOPES);
        if (!result.ok) return { ok: false as const, problem: result.error };

        invalidateHistory(client, grantKey);
        return { ok: true as const };
      }),
    [client, grantKey, run],
  );

  const clear = useCallback(
    async (): Promise<WriteResult<AuthApiProblem>> =>
      await run(CLEAR_KEY, async () => {
        const boot = await getPlatform();
        if (!boot.ok) return { ok: false as const, problem: BAD_PLATFORM };

        const result = await clearHistory(boot.platform, VISIOUS_SCOPES);
        if (!result.ok) return { ok: false as const, problem: result.error };

        invalidateHistory(client, grantKey);
        return { ok: true as const };
      }),
    [client, grantKey, run],
  );

  return { remove, clear, busy, clearing: busy.has(CLEAR_KEY) };
}

/**
 * §8's watched-state mirror, as the one call the Watch page makes.
 *
 * Separate from the two above because its failure handling is inverted: **a failure here is silent.** §8
 * says "never retried on 403", and the reasoning generalises to every failure — the reader is watching a
 * video, they did not ask for anything, and a toast saying "could not record this in your history" is an
 * interruption about a side effect they never requested. The call is fire-and-forget by design; what it
 * does on success is mark the list stale so the History page is right when they next open it.
 */
export function useMarkWatched(): (videoId: string) => Promise<void> {
  const client = useQueryClient();
  const grantKey = useGrantKey();

  return useCallback(
    async (videoId: string) => {
      const boot = await getPlatform();
      if (!boot.ok) return;

      const result = await markWatched(boot.platform, videoId, VISIOUS_SCOPES);
      // Deliberately unreported. The one thing worth doing on success is *not* refetching — see
      // `markHistoryStale`.
      if (result.ok) markHistoryStale(client, grantKey);
    },
    [client, grantKey],
  );
}
